/* =============================================================
   9 STRAP — PDP Pack Picker (Sheffield-derived)
   - Single-product PDP context with cross-model bundle slots
   - Category-aware (baseball/golf/pickleball) — customers can mix models in a pack
   - URL-based ATC (no theme-form race conditions)
   Built 2026-05-28

   AUDIT FIX 2026-05-31 v2:
   - bundleTotal applies the pack-tier % (2-Pack 15%, 4-Pack 20%, 9-Pack 25%)
     to the WHOLE bundle regardless of mix. Cart-level Shopify auto-discounts
     (qty 2+/4+/9+ from baseball/golf/pickleball collection → matching %)
     must exist to deliver these savings at checkout.
   - After successful /cart/add.js the picker fires Meta Pixel, GA4, TikTok,
     Shopify Web Pixels and the theme's cart-drawer events.
   - Adds a sticky mobile ATC bar so the buy button is reachable above the fold.

   REWRITE 2026-09-09 — GALLERY:
     The old single-image hero swap is gone. Requirement changed: each COLOR
     owns a set of ~5 images, and a SIZE change must leave the gallery
     completely alone.

     One image per variant is all Shopify's native variant->image link can
     express, and the theme's hide_variants path is dead on arrival because
       theme.css: .product__media--variant:not(:first-child){display:none}
     lays out exactly one variant slide. So grouping now lives in
     assets/hst-gallery-color.js, driven by alt-text tags ("[Red] ...")
     emitted by snippets/hst-gallery-color-groups.liquid. Section setting
     "Hide variants" must be OFF.

     This file's only job is to tell that controller when the COLOR changed.
     syncGalleryColor() early-returns when state.color is unchanged, so every
     size click, pack click and bundle-slot edit is a no-op for the gallery.
     No select(), no dots, no currentPage, no index mapping — the coordinate
     space that broke the previous implementation is never touched.

     The old /products/<handle>.json fetch that built variantImage/colorImage
     maps is removed; alt tags are the source of truth now.
   ============================================================= */

(function(){
  'use strict';

  // ====== CONFIG ======
  // Pack-tier discount applied to the WHOLE bundle (mix or not). This requires
  // matching cart-level Shopify automatic discounts (qty 2+/4+/9+ from the
  // baseball/golf/pickleball collection → 15/20/25% off cart) so the cart
  // delivers what the picker label promises regardless of mix.
  const PACK_OPTIONS = [
    { n:1, tag:null, displayPct: 0  },
    { n:2, tag:null, displayPct: 15 },
    { n:4, tag:null, displayPct: 20 },
    { n:9, tag:'Team pack', displayPct: 25 }
  ];
  const COLOR_HEX = {
    'Black':'#171717','Red':'#D7263D','Orange':'#F37A1F','Yellow':'#F7C61D',
    'Green':'#37B14B','Blue':'#2057B0','Purple':'#574CD5','Pink':'#F25CA2',
    'Tan':'#C8A57A','White':'#F4F4F4'
  };

  // ====== READ PRODUCT DATA FROM EMBEDDED JSON ======
  const dataEl = document.getElementById('pdp-pack-picker-product-data-main');
  if(!dataEl){
    console.error('pdp-pack-picker: product data script tag not found');
    return;
  }
  let bundleData;
  try {
    bundleData = JSON.parse(dataEl.textContent);
  } catch(e){
    console.error('pdp-pack-picker: failed to parse product data', e);
    return;
  }
  const PRODUCTS = bundleData.products || {};
  const PRIMARY_HANDLE = bundleData.primaryHandle;
  const CATEGORY = bundleData.category;

  for(const h in PRODUCTS){
    const p = PRODUCTS[h];
    p.sizes = []; p.colors = [];
    for(const v of p.variants){
      if(v.size && !p.sizes.includes(v.size)) p.sizes.push(v.size);
      if(v.color && !p.colors.includes(v.color)) p.colors.push(v.color);
    }
    p.label = (p.title || h).replace(/^9 STRAP\s*/i, '').trim();
  }

  if(!PRODUCTS[PRIMARY_HANDLE]){
    console.warn('pdp-pack-picker: primary product not in bundle data');
    return;
  }

  const CATEGORY_HANDLES = Object.keys(PRODUCTS);

  // ====== STATE ======
  const state = {
    size: null,
    color: null,
    pack: 2,
    bundle: []
  };

  function primary(){ return PRODUCTS[PRIMARY_HANDLE]; }
  function productOf(handle){ return PRODUCTS[handle] || primary(); }
  function fmtPrice(n){ return '$' + Number(n).toFixed(2); }

  function findVariant(handle, size, color){
    const p = PRODUCTS[handle];
    if(!p) return null;
    return p.variants.find(v =>
      (size  == null || v.size  === size) &&
      (color == null || v.color === color)
    ) || null;
  }
  function currentVariant(){ return findVariant(PRIMARY_HANDLE, state.size, state.color); }
  function comboAvailable(handle, size, color){
    const v = findVariant(handle, size, color);
    return v ? v.available : false;
  }
  function pickDefaultOptions(handle){
    const p = PRODUCTS[handle];
    const firstAvail = p.variants.find(v => v.available) || p.variants[0];
    return { size: firstAvail.size || null, color: firstAvail.color || null };
  }

  // ====== BUNDLE HELPERS ======
  function ensureBundleSize(){
    if(state.pack <= 1){ state.bundle = []; return; }
    const need = state.pack - 1;
    while(state.bundle.length < need){
      state.bundle.push({ customized:false, handle:null, size:null, color:null });
    }
    if(state.bundle.length > need){
      state.bundle = state.bundle.slice(0, need);
    }
  }
  function matchAllSlotsToPrimary(){
    state.bundle = state.bundle.map(()=>({ customized:false, handle:null, size:null, color:null }));
  }
  function customizeCount(){
    return state.bundle.filter(s=>s.customized).length;
  }
  function resolveSlot(s){
    return s.customized
      ? { handle:s.handle, size:s.size, color:s.color }
      : { handle:PRIMARY_HANDLE, size:state.size, color:state.color };
  }
  function bundleItems(){
    const arr = [{ slotIndex:0, handle:PRIMARY_HANDLE, size:state.size, color:state.color }];
    for(let i=0;i<state.bundle.length;i++){
      const r = resolveSlot(state.bundle[i]);
      arr.push({ slotIndex:i+1, ...r });
    }
    return arr.map(it=>{
      const v = findVariant(it.handle, it.size, it.color);
      return {
        ...it,
        variant: v,
        price: v ? Number(v.price) : 0,
        title: (productOf(it.handle).label) || ''
      };
    });
  }

  // === AUDIT FIX 2026-05-31 v2 — pack-tier discount applies to TOTAL bundle ===
  function activePackTier(){
    return PACK_OPTIONS.find(o => o.n === state.pack) || PACK_OPTIONS[0];
  }
  function bundleSubtotal(){
    return bundleItems().reduce((s, i)=> s + i.price, 0);
  }
  function bundleTotal(){
    return bundleSubtotal() * (1 - activePackTier().displayPct/100);
  }

  // ====== ELEMENT REFERENCES ======
  const els = {
    sizeRow:     document.getElementById('pdp-size-row'),
    sizeValues:  document.getElementById('pdp-size-values'),
    sizeSel:     document.getElementById('pdp-size-sel'),
    colorRow:    document.getElementById('pdp-color-row'),
    colorValues: document.getElementById('pdp-color-values'),
    colorSel:    document.getElementById('pdp-color-sel'),
    packRow:     document.getElementById('pdp-pack-row'),
    packSel:     document.getElementById('pdp-pack-sel'),
    packValues:  document.getElementById('pdp-pack-values'),
    bundle:      document.getElementById('pdp-bundle'),
    bundleSel:   document.getElementById('pdp-bundle-sel'),
    bundleSlots: document.getElementById('pdp-bundle-slots'),
    bundleMatch: document.getElementById('pdp-bundle-match'),
    stockPack:   document.getElementById('pdp-stock-pack'),
    atc:         document.getElementById('pdp-atc'),
    atcPrice:    document.getElementById('pdp-atc-price'),
    buy:         document.getElementById('pdp-buy')
  };

  // ====== RENDER ======
  function render(){
    ensureBundleSize();
    const p = primary();
    const v = currentVariant();
    const unitPrice = v ? Number(v.price) : Number(p.variants[0].price);

    // --- size ---
    if(p.sizes && p.sizes.length){
      els.sizeRow.style.display = '';
      els.sizeSel.textContent = state.size || '';
      els.sizeValues.innerHTML = p.sizes.map(sz=>{
        const ok = comboAvailable(PRIMARY_HANDLE, sz, state.color);
        const act = sz === state.size;
        return `<button type="button" class="pdp-opt ${act?'is-active':''} ${!ok?'is-oos':''}" data-size="${sz}">${sz}</button>`;
      }).join('');
      els.sizeValues.querySelectorAll('.pdp-opt').forEach(b=>{
        b.addEventListener('click', ()=>{
          const sz = b.dataset.size;
          if(!comboAvailable(PRIMARY_HANDLE, sz, state.color)){
            // Only case where a size click can move the gallery: the chosen
            // size doesn't exist in the current color, so we must switch color.
            const alt = p.colors.find(c => comboAvailable(PRIMARY_HANDLE, sz, c));
            if(alt){ state.size = sz; state.color = alt; } else return;
          } else state.size = sz;
          render();
        });
      });
    } else els.sizeRow.style.display = 'none';

    // --- color ---
    if(p.colors && p.colors.length){
      els.colorRow.style.display = '';
      els.colorSel.textContent = state.color || '';
      els.colorValues.innerHTML = p.colors.map(c=>{
        const ok  = comboAvailable(PRIMARY_HANDLE, state.size, c);
        const act = c === state.color;
        const hex = COLOR_HEX[c] || '#666';
        return `<button type="button" class="pdp-swatch ${act?'is-active':''} ${!ok?'is-oos':''}" style="background:${hex}" data-color="${c}" aria-label="${c}" title="${c}"></button>`;
      }).join('');
      els.colorValues.querySelectorAll('.pdp-swatch').forEach(b=>{
        b.addEventListener('click', ()=>{
          const c = b.dataset.color;
          if(!comboAvailable(PRIMARY_HANDLE, state.size, c)){
            const alt = p.sizes.find(sz => comboAvailable(PRIMARY_HANDLE, sz, c));
            if(alt){ state.size = alt; state.color = c; } else return;
          } else state.color = c;
          render();
        });
      });
    } else els.colorRow.style.display = 'none';

    // --- pack picker ---
    els.packRow.style.display = '';
    els.packSel.textContent = `${state.pack}-pack`;
    els.packValues.innerHTML = PACK_OPTIONS.map(opt=>{
      const isAct = opt.n === state.pack;
      const sub = unitPrice * opt.n;
      const tot = sub * (1 - opt.displayPct/100);
      const tag = opt.tag ? `<span class="pack-tag">${opt.tag}</span>` : '';
      const saveBadge = (!opt.tag && opt.displayPct > 0) ? `<span class="pack-save">Save ${opt.displayPct}%</span>` : '';
      const priceHtml = opt.displayPct > 0
        ? `<span class="pack-price-row"><span class="pack-price">${fmtPrice(tot)}</span><span class="pack-strike">${fmtPrice(sub)}</span></span>`
        : `<span class="pack-price-row"><span class="pack-price">${fmtPrice(sub)}</span></span>`;
      const eachLine = opt.n > 1
        ? `<span class="pack-each">${fmtPrice(unitPrice * (1 - opt.displayPct/100))} ea</span>`
        : `<span class="pack-each">&nbsp;</span>`;
      return `<button type="button" class="pdp-pack ${isAct?'is-active':''}" data-pack="${opt.n}">${tag||saveBadge}<span class="pack-count">${opt.n}-Pack</span>${priceHtml}${eachLine}</button>`;
    }).join('');
    els.packValues.querySelectorAll('.pdp-pack').forEach(b=>{
      b.addEventListener('click', ()=>{
        state.pack = parseInt(b.dataset.pack,10);
        state.bundle = [];
        render();
      });
    });

    // --- bundle slots ---
    if(state.pack > 1){
      els.bundle.style.display = '';
      els.bundleSel.textContent = `${state.pack} straps · mix & match`;
      renderBundleSlots();
      els.bundleMatch.style.visibility = customizeCount() > 0 ? '' : 'hidden';
    } else {
      els.bundle.style.display = 'none';
    }

    // --- stock + ATC ---
    const itemsAvail = bundleItems().every(it => it.variant && it.variant.available);
    // 2026-08-04: "ships today" line + 6h-rolling countdown. Timer text is
    // filled by the section-level tick loop so re-renders don't kill it.
    const stockMsg = itemsAvail
      ? `<span class="dot"></span><span class="pdp-stock-text">In stock — ships today</span><span class="pdp-stock-timer" data-hst-ship-timer></span>`
      : `<span class="dot"></span>A selection is sold out`;
    if(els.stockPack) els.stockPack.innerHTML = stockMsg;
    if(els.stockPack) els.stockPack.classList.toggle('oos', !itemsAvail);

    els.atc.disabled = !itemsAvail;
    els.buy.disabled = !itemsAvail;
    els.atc.style.opacity = itemsAvail ? '' : '.5';
    els.buy.style.opacity = itemsAvail ? '' : '.5';
    els.atc.style.cursor = itemsAvail ? '' : 'not-allowed';
    els.buy.style.cursor = itemsAvail ? '' : 'not-allowed';

    const total = bundleTotal();
    const atcLabel = state.pack === 1
      ? `Add to cart • ${fmtPrice(total)}`
      : `Add ${state.pack}-Pack to cart • ${fmtPrice(total)}`;
    els.atc.innerHTML = atcLabel;

    // AUDIT FIX 2026-05-31 — keep sticky bar in sync each render
    syncStickyBar(itemsAvail, total);

    // 2026-09-09 — hand the color to the gallery controller. No-ops unless
    // the color actually changed, which is what makes size clicks inert.
    syncGalleryColor();
  }

  /* ===========================================================
     GALLERY COLOR SYNC
     The entire "which images are showing" problem lives in
     assets/hst-gallery-color.js. All this does is announce a color change,
     and only when there is one. Wrapped in try/catch: a gallery markup
     change must never break the buy box again.
     =========================================================== */
  let lastGalleryColor = null;
  function syncGalleryColor(){
    try {
      if(state.color === lastGalleryColor) return;   // size/pack/bundle change → no-op
      lastGalleryColor = state.color;
      const g = window.HSTGalleryColor;
      if(g && typeof g.setColor === 'function') g.setColor(state.color);
    } catch(e){
      console.warn('pdp-pack-picker: gallery color sync failed', e);
    }
  }

  function renderBundleSlots(){
    const html = state.bundle.map((slot, idx)=>{
      const slotNo = idx + 2;
      const r = resolveSlot(slot);
      const sp = productOf(r.handle);
      const sv = findVariant(r.handle, r.size, r.color);
      const price = sv ? fmtPrice(sv.price) : '—';

      if(!slot.customized){
        const summaryBits = [sp.label, r.size, r.color].filter(Boolean).join(' · ');
        return `
          <div class="bundle-slot is-collapsed" data-slot="${idx}">
            <div class="bundle-summary">
              <span class="bundle-slot-label">Strap ${slotNo}</span>
              <span class="bundle-summary-text"><span class="match-tag">matches Strap 1</span><span class="bundle-summary-detail">${summaryBits}</span></span>
            </div>
            <button type="button" class="bundle-customize" data-slot="${idx}" data-action="customize">Change <span class="arr">→</span></button>
          </div>`;
      }

      const slotProduct = productOf(slot.handle);
      const modelBtns = CATEGORY_HANDLES.length > 1
        ? `<div class="bundle-slot-row"><span class="lbl">Model</span><div class="row">` +
          CATEGORY_HANDLES.map(h => {
            const act = h === slot.handle;
            const lbl = PRODUCTS[h].label;
            return `<button type="button" class="pdp-opt sm ${act?'is-active':''}" data-slot="${idx}" data-action="model" data-value="${h}">${lbl}</button>`;
          }).join('') +
          `</div></div>`
        : '';
      const sizeBtns = (slotProduct.sizes || []).map(sz=>{
        const ok = !!(findVariant(slot.handle, sz, slot.color)?.available);
        const act = sz === slot.size;
        return `<button type="button" class="pdp-opt sm ${act?'is-active':''} ${!ok?'is-oos':''}" data-slot="${idx}" data-action="size" data-value="${sz}">${sz}</button>`;
      }).join('');
      const colorBtns = (slotProduct.colors || []).map(c=>{
        const ok = !!(findVariant(slot.handle, slot.size, c)?.available);
        const act = c === slot.color;
        const hex = COLOR_HEX[c] || '#666';
        return `<button type="button" class="pdp-swatch sm ${act?'is-active':''} ${!ok?'is-oos':''}" style="background:${hex}" data-slot="${idx}" data-action="color" data-value="${c}" title="${c}" aria-label="${c}"></button>`;
      }).join('');
      return `
        <div class="bundle-slot is-expanded" data-slot="${idx}">
          <div class="bundle-slot-head">
            <span class="bundle-slot-label">Strap ${slotNo} <span class="bundle-slot-model">${slotProduct.label}</span></span>
            <span class="bundle-slot-price">${price}</span>
            <button type="button" class="bundle-reset" data-slot="${idx}" data-action="reset-slot" title="Reset to match Strap 1">&#x21bb;</button>
          </div>
          ${modelBtns}
          <div class="bundle-slot-row"><span class="lbl">Size</span><div class="row">${sizeBtns}</div></div>
          <div class="bundle-slot-row"><span class="lbl">Color</span><div class="row">${colorBtns}</div></div>
        </div>`;
    }).join('');
    els.bundleSlots.innerHTML = html;

    els.bundleSlots.querySelectorAll('button[data-action]').forEach(b=>{
      b.addEventListener('click', ()=>{
        const idx = parseInt(b.dataset.slot,10);
        const action = b.dataset.action;
        const value = b.dataset.value;
        const slot = state.bundle[idx];

        if(action === 'customize'){
          slot.customized = true;
          slot.handle = PRIMARY_HANDLE;
          slot.size = state.size;
          slot.color = state.color;
          render();
          return;
        }
        if(action === 'reset-slot'){
          slot.customized = false;
          slot.handle = null; slot.size = null; slot.color = null;
          render();
          return;
        }
        if(action === 'model'){
          const targetP = PRODUCTS[value];
          if(!targetP) return;
          slot.handle = value;
          const sizeOk  = targetP.sizes.includes(slot.size);
          const colorOk = targetP.colors.includes(slot.color);
          if(!sizeOk || !colorOk){
            const def = pickDefaultOptions(value);
            slot.size  = sizeOk  ? slot.size  : def.size;
            slot.color = colorOk ? slot.color : def.color;
          }
          const v = findVariant(slot.handle, slot.size, slot.color);
          if(!v || !v.available){
            const def = pickDefaultOptions(slot.handle);
            slot.size = def.size; slot.color = def.color;
          }
        } else if(action === 'size'){
          const v = findVariant(slot.handle, value, slot.color);
          if(!v || !v.available){
            const alt = PRODUCTS[slot.handle].colors.find(c => findVariant(slot.handle, value, c)?.available);
            if(alt){ slot.size = value; slot.color = alt; } else return;
          } else slot.size = value;
        } else if(action === 'color'){
          const v = findVariant(slot.handle, slot.size, value);
          if(!v || !v.available){
            const alt = PRODUCTS[slot.handle].sizes.find(sz => findVariant(slot.handle, sz, value)?.available);
            if(alt){ slot.size = alt; slot.color = value; } else return;
          } else slot.color = value;
        }
        render();
      });
    });
  }

  // ====== ACTIONS ======
  async function postCartItems(){
    const items = bundleItems().filter(i => i.variant && i.variant.available);
    if(items.length === 0) return { ok:false, items: [] };
    const counts = {};
    for(const it of items){
      counts[it.variant.id] = (counts[it.variant.id] || 0) + 1;
    }
    const body = { items: Object.entries(counts).map(([id,q]) => ({ id: parseInt(id,10), quantity: q })) };
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body),
        credentials: 'same-origin'
      });
      if(!r.ok) return { ok:false, items: [] };
      const responseBody = await r.json().catch(()=>null);
      return { ok:true, items, response: responseBody };
    } catch(e){ return { ok:false, items: [] }; }
  }

  // === AUDIT FIX 2026-05-31 — fire all the tracking pixels the picker was bypassing ===
  function fireTrackingEvents(addedItems, responseBody){
    try {
      const cartValue = bundleTotal();
      const variantIds = [];
      const contents = [];
      for(const it of addedItems){
        variantIds.push(String(it.variant.id));
        contents.push({ id: String(it.variant.id), quantity: 1, item_price: Number(it.variant.price) });
      }
      if(typeof window.fbq === 'function'){
        window.fbq('track', 'AddToCart', {
          content_ids: variantIds,
          content_type: 'product_variant',
          contents,
          value: Number(cartValue.toFixed(2)),
          currency: 'USD',
          num_items: addedItems.length
        });
      }
      if(window.dataLayer && typeof window.dataLayer.push === 'function'){
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'add_to_cart',
          ecommerce: {
            currency: 'USD',
            value: Number(cartValue.toFixed(2)),
            items: addedItems.map((it, ix) => ({
              item_id: String(it.variant.sku || it.variant.id),
              item_variant: String(it.variant.id),
              item_name: it.title,
              price: Number(it.variant.price),
              quantity: 1,
              index: ix
            }))
          }
        });
      }
      if(window.ttq && typeof window.ttq.track === 'function'){
        window.ttq.track('AddToCart', {
          contents: addedItems.map(it => ({
            content_id: String(it.variant.id),
            content_type: 'product_variant',
            content_name: it.title,
            quantity: 1,
            price: Number(it.variant.price)
          })),
          value: Number(cartValue.toFixed(2)),
          currency: 'USD'
        });
      }
      if(window.webPixelsManager && typeof window.webPixelsManager.publish === 'function'){
        for(const it of addedItems){
          try {
            window.webPixelsManager.publish('product_added_to_cart', {
              cartLine: {
                cost: { totalAmount: { amount: Number(it.variant.price), currencyCode: 'USD' } },
                merchandise: {
                  id: 'gid://shopify/ProductVariant/' + it.variant.id,
                  price: { amount: Number(it.variant.price), currencyCode: 'USD' },
                  product: { id: 'gid://shopify/Product/' + (PRODUCTS[it.handle]?.id || ''), title: it.title, vendor: '9 STRAP TECHNOLOGY' },
                  title: it.title
                },
                quantity: 1
              }
            });
          } catch(_){}
        }
      }
      const evtDetail = { source: 'pdp-pack-picker', items: addedItems.map(it=>({id:it.variant.id, qty:1, price:it.variant.price})), value: cartValue, currency: 'USD', response: responseBody };
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: evtDetail, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:updated',    { detail: evtDetail, bubbles: true }));
      window.dispatchEvent(new CustomEvent('cart:item-added',   { detail: evtDetail }));
      window.dispatchEvent(new CustomEvent('cart:updated',      { detail: evtDetail }));
      if(window._learnq){
        try { window._learnq.push(['track', 'Added to Cart', { Value: cartValue, ItemNames: addedItems.map(it=>it.title) }]); } catch(_){}
      }
    } catch(e){
      console.warn('pdp-pack-picker: tracking dispatch failed', e);
    }
  }

  async function addToCart(){
    els.atc.disabled = true;
    const result = await postCartItems();
    if(result.ok){
      fireTrackingEvents(result.items, result.response);
      window.location.href = '/cart';
    } else {
      els.atc.disabled = false;
      alert("Sorry, we couldn't add those items to your cart. Please try again.");
    }
  }
  async function buyNow(){
    els.buy.disabled = true;
    const result = await postCartItems();
    if(result.ok){
      fireTrackingEvents(result.items, result.response);
      window.location.href = '/checkout';
    } else {
      els.buy.disabled = false;
      alert("Sorry, we couldn't proceed to checkout. Please try again.");
    }
  }

  els.atc.addEventListener('click', addToCart);
  els.buy.addEventListener('click', buyNow);
  els.bundleMatch.addEventListener('click', ()=>{ matchAllSlotsToPrimary(); render(); });

  // === AUDIT FIX 2026-05-31 — sticky mobile ATC bar ===
  let stickyBar = null;
  function buildStickyBar(){
    if(stickyBar) return stickyBar;
    stickyBar = document.createElement('div');
    stickyBar.id = 'pdp-sticky-atc';
    stickyBar.className = 'pdp-sticky-atc';
    stickyBar.setAttribute('role', 'region');
    stickyBar.setAttribute('aria-label', 'Sticky add to cart');
    stickyBar.innerHTML =
      '<button type="button" id="pdp-sticky-atc-btn" class="pdp-sticky-atc-btn">' +
      '  <span class="pdp-sticky-label">Add to cart</span>' +
      '  <span class="pdp-sticky-price">—</span>' +
      '</button>';
    document.body.appendChild(stickyBar);
    stickyBar.querySelector('#pdp-sticky-atc-btn').addEventListener('click', () => {
      const inlineATC = document.getElementById('pdp-atc');
      if(!inlineATC) return;
      const r = inlineATC.getBoundingClientRect();
      const inView = r.top >= 0 && r.bottom <= window.innerHeight;
      if(inView){
        inlineATC.click();
      } else {
        inlineATC.scrollIntoView({ behavior: 'smooth', block: 'center' });
        inlineATC.classList.add('pulse');
        setTimeout(() => inlineATC.classList.remove('pulse'), 1200);
      }
    });
    const inlineATC = document.getElementById('pdp-atc');
    if(inlineATC && typeof IntersectionObserver !== 'undefined'){
      const io = new IntersectionObserver(entries => {
        for(const e of entries){
          stickyBar.classList.toggle('is-hidden-by-inline', e.isIntersecting);
        }
      }, { threshold: 0.6 });
      io.observe(inlineATC);
    }
    return stickyBar;
  }
  function syncStickyBar(itemsAvail, total){
    const bar = buildStickyBar();
    if(!bar) return;
    const lblEl = bar.querySelector('.pdp-sticky-label');
    const priceEl = bar.querySelector('.pdp-sticky-price');
    const btn = bar.querySelector('#pdp-sticky-atc-btn');
    lblEl.textContent = state.pack === 1 ? 'Add to cart' : `Add ${state.pack}-Pack to cart`;
    priceEl.textContent = '• ' + fmtPrice(total);
    btn.disabled = !itemsAvail;
    bar.classList.toggle('is-disabled', !itemsAvail);
  }

  // ====== SIZE HELP ACCORDION ======
  (function initSizeHelp(){
    const trigger = document.getElementById('size-help-trigger');
    const panel   = document.getElementById('size-help-panel');
    if(!trigger || !panel) return;
    trigger.addEventListener('click', ()=>{
      const open = !trigger.classList.contains('is-open');
      trigger.classList.toggle('is-open', open);
      panel.classList.toggle('is-open', open);
      trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.setAttribute('aria-hidden', open ? 'false' : 'true');
      if(open){
        setTimeout(()=>panel.scrollIntoView({behavior:'smooth', block:'nearest'}), 220);
      }
    });
  })();

  // ====== INIT ======
  const def = pickDefaultOptions(PRIMARY_HANDLE);
  state.size = def.size;
  state.color = def.color;
  render();

})();